import { Button } from '@fluentui/react-components';
import { Logo } from '../common/Logo';
import './AuthExperience.css';

export type AuthExperienceMode = 'loading' | 'sign-in' | 'error';

interface AuthExperienceProps {
  mode: AuthExperienceMode;
  status?: string;
  error?: string | null;
  onSignIn?: () => void;
  onRetry?: () => void;
}
export function getAuthStatusLabel(status?: string) {
  const normalized = status?.toLowerCase() ?? '';

  if (normalized.includes('token') || normalized.includes('sign-in')) {
    return 'Securing your Microsoft sign-in…';
  }
  if (normalized.includes('teams')) {
    return 'Connecting to Microsoft Teams…';
  }
  if (normalized.includes('profile') || normalized.includes('user')) {
    return 'Preparing your VoiceIQ view…';
  }
  return 'Opening VoiceIQ…';
}

function MicrosoftMark() {
  return (
    <span className="authExperience__microsoftMark" aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}

function VoiceIQMark({ compact = false }: { compact?: boolean }) {
  return (
    <svg
      className={compact ? 'authExperience__resolvedMark authExperience__resolvedMark--compact' : 'authExperience__resolvedMark'}
      viewBox="0 0 180 180"
      role="img"
      aria-label="VoiceIQ"
    >
      <defs>
        <radialGradient id={compact ? 'mark-surface-compact' : 'mark-surface'} cx="34%" cy="25%" r="78%">
          <stop offset="0" stopColor="#f8ffff" />
          <stop offset="0.42" stopColor="#d9fffa" />
          <stop offset="1" stopColor="#5eead4" />
        </radialGradient>
        <filter id={compact ? 'mark-glow-compact' : 'mark-glow'} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="7" result="blur" />
          <feColorMatrix in="blur" values="0 0 0 0 0.18 0 0 0 0 0.91 0 0 0 0 0.82 0 0 0 .45 0" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <circle className="authExperience__markHalo" cx="90" cy="90" r="70" fill="none" stroke="#5eead4" strokeOpacity=".12" />
      <g className="authExperience__markBody" filter={`url(#${compact ? 'mark-glow-compact' : 'mark-glow'})`}>
        <circle cx="82" cy="78" r="48" fill="#071826" stroke={`url(#${compact ? 'mark-surface-compact' : 'mark-surface'})`} strokeWidth="8" />
        <path d="M59 70 82 96 105 70" fill="none" stroke="#f7fffe" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m116 112 17 17" stroke="#5eead4" strokeWidth="9" strokeLinecap="round" />
      </g>
    </svg>
  );
}

function SignalLoader() {
  return (
    <div className="authExperience__loader" aria-hidden="true">
      <svg viewBox="0 0 360 220">
        <defs>
          <linearGradient id="loader-route" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#5366df" stopOpacity=".15" />
            <stop offset=".55" stopColor="#5eead4" />
            <stop offset="1" stopColor="#bafff5" />
          </linearGradient>
          <filter id="loader-glow" x="-50%" y="-100%" width="200%" height="300%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <path className="authExperience__loaderRail" d="M18 142 C82 142 91 78 151 78 S222 142 283 142" />
        <path className="authExperience__loaderTrace" d="M18 142 C82 142 91 78 151 78 S222 142 283 142" />
        <circle className="authExperience__loaderNode authExperience__loaderNode--one" cx="72" cy="127" r="8" />
        <circle className="authExperience__loaderNode authExperience__loaderNode--two" cx="154" cy="78" r="8" />
        <circle className="authExperience__loaderNode authExperience__loaderNode--three" cx="236" cy="127" r="8" />
        <g className="authExperience__loaderMark" transform="translate(275 74) scale(.62)">
          <circle cx="60" cy="60" r="43" fill="#071826" stroke="#5eead4" strokeWidth="7" />
          <path d="M39 53 60 77 81 53" fill="none" stroke="#fff" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="m90 89 14 14" stroke="#5eead4" strokeWidth="8" strokeLinecap="round" />
        </g>
      </svg>
    </div>
  );
}

function SignalRoom() {
  return (
    <div className="authExperience__scene" aria-hidden="true" data-testid="signal-room">
      <div className="authExperience__sceneGlow" />
      <svg viewBox="0 0 820 760" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="back-wall" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#111b35" />
            <stop offset=".48" stopColor="#0c1830" />
            <stop offset="1" stopColor="#07111f" />
          </linearGradient>
          <linearGradient id="plinth-top" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#263650" />
            <stop offset="1" stopColor="#10192b" />
          </linearGradient>
          <linearGradient id="plinth-front" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#101a2d" />
            <stop offset="1" stopColor="#070d17" />
          </linearGradient>
          <linearGradient id="ribbon-face" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#22324b" />
            <stop offset=".5" stopColor="#314260" />
            <stop offset="1" stopColor="#132137" />
          </linearGradient>
          <linearGradient id="ribbon-edge" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#5d6be8" stopOpacity=".25" />
            <stop offset=".45" stopColor="#5eead4" />
            <stop offset="1" stopColor="#d5fff9" />
          </linearGradient>
          <radialGradient id="pearl" cx="40%" cy="30%" r="80%">
            <stop offset="0" stopColor="#bfe9e0" stopOpacity=".5" />
            <stop offset=".6" stopColor="#5eead4" stopOpacity=".16" />
            <stop offset="1" stopColor="#5eead4" stopOpacity="0" />
          </radialGradient>
          <clipPath id="pearl-clip">
            <circle cx="600" cy="307" r="150" />
          </clipPath>
          <filter id="soft-shadow" x="-40%" y="-40%" width="180%" height="200%">
            <feDropShadow dx="0" dy="22" stdDeviation="20" floodColor="#02050d" floodOpacity=".72" />
          </filter>
          <filter id="signal-glow" x="-60%" y="-100%" width="220%" height="300%">
            <feGaussianBlur stdDeviation="7" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <pattern id="grain" width="48" height="48" patternUnits="userSpaceOnUse">
            <circle cx="7" cy="13" r=".8" fill="#fff" opacity=".08" />
            <circle cx="29" cy="34" r=".65" fill="#fff" opacity=".06" />
            <circle cx="42" cy="9" r=".55" fill="#fff" opacity=".05" />
          </pattern>
        </defs>

        <path d="M80 78H770V588L666 682H80Z" fill="url(#back-wall)" />
        <ellipse cx="600" cy="307" rx="152" ry="152" fill="url(#pearl)" />
        <g className="authExperience__waveform" clipPath="url(#pearl-clip)" stroke="#a9f3e6" strokeWidth="4" strokeLinecap="round" strokeOpacity=".5">
          <line className="authExperience__waveBar authExperience__waveBar--1" x1="530" y1="287" x2="530" y2="327" />
          <line className="authExperience__waveBar authExperience__waveBar--2" x1="565" y1="267" x2="565" y2="347" />
          <line className="authExperience__waveBar authExperience__waveBar--3" x1="600" y1="247" x2="600" y2="367" />
          <line className="authExperience__waveBar authExperience__waveBar--4" x1="635" y1="267" x2="635" y2="347" />
          <line className="authExperience__waveBar authExperience__waveBar--5" x1="670" y1="287" x2="670" y2="327" />
        </g>
        <ellipse cx="597" cy="470" rx="154" ry="30" fill="#5e64ef" opacity=".2" />

        <g filter="url(#soft-shadow)">
          <path d="m92 542 455-78 176 91-446 99z" fill="url(#plinth-top)" />
          <path d="m277 654 446-99v55L278 715z" fill="url(#plinth-front)" />
          <path d="M92 542v53l186 120v-61z" fill="#0b1322" />
        </g>

        <path d="M118 529c126-88 174-99 267-45 87 51 123 76 278 41" fill="none" stroke="#07101c" strokeWidth="72" strokeLinecap="round" />
        <path d="M118 510c126-88 174-99 267-45 87 51 123 76 278 41" fill="none" stroke="url(#ribbon-face)" strokeWidth="70" strokeLinecap="round" />
        <path d="M118 478c126-88 174-99 267-45 87 51 123 76 278 41" fill="none" stroke="url(#ribbon-edge)" strokeWidth="3" strokeLinecap="round" opacity=".9" />
        <path className="authExperience__signalPulse" d="M118 478c126-88 174-99 267-45 87 51 123 76 278 41" fill="none" stroke="#d9fffa" strokeWidth="8" strokeLinecap="round" pathLength="1" filter="url(#signal-glow)" />

        <g className="authExperience__station authExperience__station--caller" transform="translate(123 425)">
          <ellipse cx="0" cy="79" rx="57" ry="17" fill="#020712" opacity=".52" />
          <rect x="-44" y="-6" width="88" height="84" rx="22" fill="#15253c" stroke="#52617a" />
          <path d="M-18 34c11-17 25-17 36 0M-10 45c7-10 13-10 20 0" fill="none" stroke="#79eee0" strokeWidth="5" strokeLinecap="round" />
        </g>

        <g className="authExperience__station authExperience__station--queue" transform="translate(315 385)">
          <ellipse cx="0" cy="93" rx="64" ry="18" fill="#020712" opacity=".52" />
          <rect x="-51" y="-5" width="102" height="94" rx="24" fill="#182a45" stroke="#6271a0" />
          <path d="M-22 27h44M-22 43h31M-22 59h38" stroke="#93a1dd" strokeWidth="6" strokeLinecap="round" />
          <circle cx="25" cy="59" r="5" fill="#5eead4" />
        </g>

        <g className="authExperience__station authExperience__station--agent" transform="translate(500 450)">
          <ellipse cx="0" cy="91" rx="63" ry="18" fill="#020712" opacity=".52" />
          <rect x="-50" y="-7" width="100" height="94" rx="24" fill="#14243b" stroke="#52617a" />
          <circle cx="0" cy="31" r="14" fill="none" stroke="#f6fffe" strokeWidth="5" />
          <path d="M-23 64c8-17 38-17 46 0" fill="none" stroke="#f6fffe" strokeWidth="5" strokeLinecap="round" />
          <path d="M19 26c11 5 11 21 1 27" fill="none" stroke="#5eead4" strokeWidth="4" strokeLinecap="round" />
        </g>

        <g className="authExperience__markStation" transform="translate(642 430)">
          <ellipse cx="0" cy="109" rx="75" ry="20" fill="#020712" opacity=".6" />
          <circle cx="0" cy="37" r="66" fill="#071523" stroke="#5eead4" strokeWidth="6" />
          <circle cx="0" cy="37" r="55" fill="url(#grain)" opacity=".8" />
          <path d="M-27 26 0 57 27 26" fill="none" stroke="#f7fffe" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
          <path d="m38 75 19 19" stroke="#5eead4" strokeWidth="10" strokeLinecap="round" />
        </g>

        <g className="authExperience__qualityBadge" transform="translate(586 615)">
          <rect x="-96" y="-26" width="192" height="52" rx="26" fill="#0c1829" stroke="#39516a" />
          <circle cx="-65" cy="0" r="8" fill="#5eead4" />
          <path d="m-69 0 3 3 6-8" fill="none" stroke="#071523" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          <text x="-44" y="6" fill="#dcebea" fontFamily="Segoe UI, sans-serif" fontSize="15">Signal understood</text>
        </g>
      </svg>
    </div>
  );
}

export function AuthExperience({ mode, status, error, onSignIn, onRetry }: AuthExperienceProps) {
  const isLoading = mode === 'loading';
  const isError = mode === 'error';

  return (
    <div className={`authExperience authExperience--${mode}`} data-testid="auth-experience" data-mode={mode}>
      <div className="authExperience__noise" aria-hidden="true" />
      <header className="authExperience__header">
        <Logo variant="wordmark" theme="dark" height={42} href={null} />
        {!isLoading && <span className="authExperience__secureLabel">Communications intelligence</span>}
      </header>

      {isLoading ? (
        <main className="authExperience__loadingPanel">
          <SignalLoader />
          <div className="authExperience__loadingLockup">
            <VoiceIQMark compact />
            <div>
              <p className="authExperience__loadingTitle">Bringing every signal into focus</p>
              <p className="authExperience__status" role="status" aria-live="polite">{getAuthStatusLabel(status)}</p>
            </div>
          </div>
        </main>
      ) : (
        <main className="authExperience__main">
          <section className="authExperience__copy" aria-labelledby="auth-experience-title">
            <p className="authExperience__eyebrow"><span /> Communications intelligence</p>
            <h1 id="auth-experience-title">
              Every call,<br /><em>in context.</em>
            </h1>
            <p className="authExperience__lede">
              Understand calling activity, queue performance, and service quality in one clear operational view.
            </p>

            {isError ? (
              <div className="authExperience__actionGroup">
                <div className="authExperience__error" role="alert">
                  <strong>We couldn’t complete sign-in.</strong>
                  <span>{error || 'Please try connecting to Microsoft again.'}</span>
                </div>
                <Button className="authExperience__primaryButton" appearance="primary" size="large" onClick={onRetry}>
                  Try again
                </Button>
              </div>
            ) : (
              <div className="authExperience__actionGroup">
                <Button className="authExperience__primaryButton" appearance="primary" size="large" icon={<MicrosoftMark />} onClick={onSignIn}>
                  Sign in with Microsoft
                </Button>
                <p className="authExperience__privacyNote">Secure sign-in with Microsoft Entra ID. VoiceIQ never sees your password.</p>
              </div>
            )}
          </section>

          <SignalRoom />
        </main>
      )}

      <footer className="authExperience__footer">
        <span>VoiceIQ</span>
        <span aria-hidden="true">•</span>
        <span>A Logic V product</span>
      </footer>
    </div>
  );
}
